import React, { FC, useEffect, useState } from "react";
import "./AddUserModal.scss";
import style from "./AddUserModal.module.scss";
import { PlanLimitReachedModal } from "../../PlanLimitReachedModal/PlanLimitReachedModal";
import Typhography from "../../../shared/Typhography/Typhography";
import { useForm, FormProvider, useFormContext } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { USER_STATES } from "../../../models/User";
import CustomButton from "../../../shared/CustomButton/CustomButton";
import { useEndpoints } from "../../../hooks/useEndpoints";
import { IUser2 } from "../../../models/user copy";
import { useAppSelector } from "../../../store";
import { USER_TYPES } from "../../../enums/user";
import GenericModalV2 from "../../../shared/GenericModal/GenericModal";
import { Dialog } from "@chakra-ui/react";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import {
  genericLoadOptions,
  getLabelFromOptions,
} from "../../../lib/DurationPicker/utils";
import {
  GENDER_TEAM_OPTIONS,
  LEVELS_SPORT_OPTIONS,
  ROLES_SPORT_OPTIONS,
  USER_STATE_OPTIONS,
  USER_TYPE_OPTIONS,
} from "../../../constants/config";
import { ITeam } from "../../../models/Team";
import { GroupBase, OptionProps } from "react-select";
import classNames from "classnames";
import { CheckIcon } from "@phosphor-icons/react";
import { TypeSpaceIcon } from "../../../shared/Icons/Icons";
import { LEVELS_BY_SPORT, SPACE_TYPES } from "../../../enums/Space";
import { CustomFormV3 } from "../../../shared/CustomFormV3/CustomForm";
import { FieldDef } from "../../../shared/CustomFormV3/types";
import {
  applyServerErrors,
  toastResponseWarning,
} from "../../../shared/CustomFormV3/serverErrors";

interface IAddUserModal {
  fetch: any;
  isOpen: IUser2 | true | undefined;
  onClose: () => void;
}

export type IAddUserForm = {
  teams: any[];
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  spaceId: any;
  roles: string[];
  state: USER_STATES;
  birthdayDate: Date | null;
  type: string | null;
  gender: string | null;
};

const schema = yup.object({
  firstName: yup
    .string()
    .trim()
    .min(1, "Il nome è obbligatorio")
    .required("Il nome è obbligatorio"),
  lastName: yup
    .string()
    .trim()
    .min(1, "Il cognome è obbligatorio")
    .required("Il cognome è obbligatorio"),
  phone: yup
    .string()
    .nullable()
    .test("phone-valid", "Numero di telefono non valido", (value) => {
      if (!value) return true;
      return /^\+?[\d\s\-().]{7,20}$/.test(value);
    }),
  spaceId: yup
    .mixed()
    .nullable()
    .test("space-required", "Lo spazio è obbligatorio", function (value) {
      const { type } = this.parent;
      if (type === USER_TYPES.ADMINISTRATOR) return true;
      if (type === USER_TYPES.COACH)
        return Array.isArray(value) && value.length > 0;
      return !!value;
    }),
  gender: yup
    .string()
    .nullable()
    .required("Il genere è obbligatorio"),
  teams: yup
    .array()
    .test("teams-required", "Seleziona almeno una squadra", function (value) {
      const { type } = this.parent;
      if (type === USER_TYPES.ADMINISTRATOR) return true;
      return Array.isArray(value) && value.length > 0;
    }),
});

const initialFormValue: IAddUserForm = {
  teams: [],
  type: USER_TYPES.ATHLETE,
  firstName: "",
  email: "",
  lastName: "",
  phone: "",
  birthdayDate: null,
  spaceId: null,
  state: USER_STATES.ACTIVE,
  roles: [],
  gender: null,
};

// Custom team option that reads selected teams from RHF context
export const CustomTeamOptionRHF: React.ComponentType<
  OptionProps<any, boolean, GroupBase<any>>
> = ({ innerProps, isDisabled, data }) => {
  const { watch } = useFormContext();
  const teams = watch("teams");
  if (isDisabled) return null;
  const { name, level, spaceId, _id } = data.value;
  const isSelected = (teams as any[])?.some((item: any) => item._id === _id);
  const { type } = spaceId;
  return (
    <div
      className={`container__custom__select__input__option ${classNames({
        not__selected: !isSelected,
      })}`}
      {...innerProps}
    >
      {isSelected && <CheckIcon />}
      <TypeSpaceIcon type={type as any} />
      <span className="label text-medium-16">
        {name}{" "}
        {`( ${getLabelFromOptions(
          LEVELS_SPORT_OPTIONS[type as keyof typeof LEVELS_BY_SPORT],
          level,
        )} )`}
      </span>
    </div>
  );
};

const AddUserModal: FC<IAddUserModal> = ({ isOpen, onClose, fetch }) => {
  const { postUser, patchUser, getSpaces, getTeams } = useEndpoints();
  const selectedEntity = useAppSelector((st) => st.me.selectedEntity);
  const isEditMode = !!isOpen && typeof isOpen !== "boolean";
  const [planLimitError, setPlanLimitError] = useState<{ planName: string; current: number; max: number } | null>(null);

  const methods = useForm<IAddUserForm>({
    defaultValues: initialFormValue,
    resolver: yupResolver(schema) as any,
  });
  const { watch, reset } = methods;
  const [type, spaceId, teams] = watch(["type", "spaceId", "teams"]);

  const isDisableSaveButton =
    (type === USER_TYPES.ATHLETE && (!spaceId || !(teams as any[])?.length)) ||
    (type === USER_TYPES.COACH &&
      (!(spaceId as any[])?.length || !(teams as any[])?.length));

  useEffect(() => {
    if (isOpen && typeof isOpen !== "boolean") {
      const user = isOpen;
      const userProperties =
        user.type === USER_TYPES.ADMINISTRATOR
          ? { spaceId: null, roles: [], teams: [] }
          : user.type === USER_TYPES.COACH
            ? {
                spaceId: (user.spaceId as any).map((value: any) => ({
                  value,
                  label: `${value.name} ( ${
                    getLabelFromOptions(GENDER_TEAM_OPTIONS, value.gender) || ""
                  } )`,
                })),
                roles: [],
                teams: user.teams as any,
              }
            : {
                spaceId: user.spaceId?.[0]
                  ? {
                      label: (user.spaceId[0] as any).name,
                      value: user.spaceId[0],
                    }
                  : null,
                roles: user.roles,
                teams: user.teams as any,
              };
      reset({
        type: user.type,
        firstName: user.firstName,
        email: user.email,
        lastName: user.lastName,
        phone: user.phone,
        birthdayDate: user.birthdayDate
          ? dayjs(user.birthdayDate).toDate()
          : null,
        gender: (user as any).gender || null,
        state: USER_STATES.ACTIVE,
        ...userProperties,
      });
    }
    if (!isOpen) {
      reset(initialFormValue);
    }
  }, [isOpen, reset]);

  const onSave = async (form: any) => {
    const fullName = `${form.firstName} ${form.lastName}`;
    const id = toast.loading(
      `${isEditMode ? "Modifica" : "Aggiunta"} ${fullName} in corso...`,
    );
    try {
      const { spaceId: spaceIdVal, teams: teamsToMap, ...otherKeys } = form;
      const teamsIds = teamsToMap.map(({ _id }: any) => _id);
      let spaceIdToSave;
      if (form.type === USER_TYPES.ADMINISTRATOR) {
        spaceIdToSave = undefined;
      } else if (form.type === USER_TYPES.ATHLETE) {
        spaceIdToSave = [(spaceIdVal as any)?.value?._id];
      } else {
        spaceIdToSave = (spaceIdVal as any[]).map((item) => item.value._id);
      }
      const body: any = {
        ...otherKeys,
        spaceId: spaceIdToSave,
        entityId: selectedEntity!._id,
        teams: teamsIds,
      };
      if (body.type === USER_TYPES.ADMINISTRATOR) {
        delete body.spaceId;
        delete body.teams;
        delete body.roles;
      }
      if (body.type === USER_TYPES.COACH) {
        delete body.roles;
      }
      let response;
      if (isEditMode) {
        response = await patchUser((isOpen as IUser2)?._id, body);
      } else {
        response = await postUser(body);
      }
      toast.success(`${fullName} ${isEditMode ? "modificato" : "aggiunto"}`, {
        id,
      });
      // Warning non bloccanti (es. utente creato ma email conferma fallita)
      toastResponseWarning(response?.data);
      fetch();
      onClose();
    } catch (error: any) {
      if (error?.response?.data?.code === 'PLAN_LIMIT_REACHED') {
        toast.dismiss(id);
        setPlanLimitError({
          planName: error.response.data.planName ?? 'corrente',
          current: error.response.data.currentCount,
          max: error.response.data.maxAthletes,
        });
        return;
      }
      // 400 di validazione: errori sotto ai campi via RHF
      if (applyServerErrors(error, methods.setError)) {
        toast.error("Controlla i campi evidenziati", { id });
        return;
      }
      const errorMessage =
        typeof error?.response?.data?.message === "string"
          ? error.response.data.message
          : "Qualcosa è andato storto";
      toast.error(errorMessage, { id });
    }
  };

  const promiseOptionsSpace = genericLoadOptions(getSpaces, (group) => ({
    label: `${group.name} ( ${
      getLabelFromOptions(GENDER_TEAM_OPTIONS, group.gender) || ""
    } )`,
    value: group,
  }));

  const personalInfoLayout: FieldDef[] = [
    {
      key: "firstName",
      type: "text-input",
      label: "Nome",
      placeholder: "Inserisci nome",
      size: 6,
    },
    {
      key: "lastName",
      type: "text-input",
      label: "Cognome",
      placeholder: "Inserisci cognome",
      size: 6,
    },
    {
      key: "email",
      type: "text-input",
      label: "Email",
      placeholder: "Inserisci email",
      inputType: "email",
      size: 6,
      disabled: isEditMode,
    },
    {
      key: "phone",
      type: "phone",
      label: "Telefono",
      placeholder: "Inserisci numero di telefono",
      size: 6,
    },
    {
      key: "birthdayDate",
      type: "date",
      label: "Data di nascita",
      size: 6,
    },
    {
      key: "gender",
      type: "single-select",
      label: "Genere",
      size: 6,
      options: [
        { label: "Maschio", value: "male" },
        { label: "Femmina", value: "female" },
      ],
      placeholder: "Seleziona genere",
    },
  ];

  const accountLayout: FieldDef[] = [
    {
      key: "type",
      type: "single-select",
      label: "Tipo Account",
      size: 6,
      options: USER_TYPE_OPTIONS,
      disabled: isEditMode,
      onAfterChange: (value, { setValue }) => {
        if (value === USER_TYPES.COACH) {
          setValue("spaceId", []);
          setValue("teams", []);
        } else if (value === USER_TYPES.ADMINISTRATOR) {
          setValue("spaceId", null);
          setValue("teams", []);
          setValue("roles", []);
        } else {
          setValue("spaceId", null);
          setValue("teams", []);
        }
      },
    },
    {
      key: "state",
      type: "single-select",
      label: "Stato",
      size: 6,
      options: USER_STATE_OPTIONS,
    },
  ];

  const assignmentLayout: FieldDef[] = [
    // spaceId for ATHLETE (single async-select)
    {
      key: "spaceId",
      type: "async-select",
      label: "Spazio *",
      size: 12,
      deps: ["type"],
      visibleIf: ({ type: t }) => t === USER_TYPES.ATHLETE,
      loadOptions: promiseOptionsSpace,
      defaultOptions: true,
      isOptionSelected: (item: any, h: any) =>
        h?.[0]?.value._id === item.value._id,
      placeholder: "Cerca uno spazio...",
      onAfterChange: (option, { setValue, getValues }) => {
        if (!option) return;
        const spaceValue = option.value;
        const currentTeams = getValues("teams");
        setValue(
          "teams",
          currentTeams.filter(
            (team: any) => spaceValue._id === team.spaceId?._id,
          ),
        );
      },
    },
    // spaceId for COACH (multi async-select)
    {
      key: "spaceId",
      type: "multi-select",
      label: "Spazi *",
      size: 12,
      deps: ["type"],
      visibleIf: ({ type: t }) => t === USER_TYPES.COACH,
      loadOptions: promiseOptionsSpace,
      defaultOptions: true,
      autoHeight: true,
      isOptionSelected: (item: any, h: any) =>
        h?.some?.((i: any) => i?.value?._id === item.value?._id) ?? false,
      placeholder: "Cerca uno o più spazio...",
      onAfterChange: (values, { setValue, getValues }) => {
        const currentTeams = getValues("teams");
        setValue(
          "teams",
          currentTeams.filter((team: any) =>
            (values as any[]).some(
              (space: any) => space.value._id === team.spaceId?._id,
            ),
          ),
        );
      },
    },
    // roles for ATHLETE
    {
      key: "roles",
      type: "multi-select",
      label: "Ruoli",
      size: 12,
      deps: ["type", "spaceId"],
      visibleIf: ({ type: t }) => t === USER_TYPES.ATHLETE,
      disabled: ({ spaceId: s }) => !s,
      options: ({ spaceId: s }) => {
        const space = (s as any)?.value;
        return space?.type
          ? ((ROLES_SPORT_OPTIONS as any)?.[space.type as SPACE_TYPES] ?? [])
          : [];
      },
      placeholder: "Seleziona Ruoli...",
    },
    // teams (select-chips), visible for non-ADMINISTRATOR
    {
      key: "teams",
      type: "select-chips",
      label: "Squadre",
      size: 12,
      deps: ["type", "spaceId"],
      visibleIf: ({ type: t }) => t !== USER_TYPES.ADMINISTRATOR,
      defaultOptions: true,
      disabled: ({ type: t, spaceId: s }) => {
        if (t === USER_TYPES.COACH) return !(s as any[])?.length;
        return !s;
      },
      loadOptionsDeps: ({ type: t, spaceId: s }): any => {
        const spaceIdParam =
          t === USER_TYPES.COACH
            ? (s as any[])
                ?.map((item: any) => item?.value?._id)
                .filter(Boolean)
                .join(",")
            : (s as any)?.value?._id;
        return genericLoadOptions(
          getTeams,
          (team: ITeam<{ type: keyof typeof LEVELS_SPORT_OPTIONS }>) => ({
            label: team.name,
            value: team,
          }),
          {
            spaceId: spaceIdParam,
            populate: "spaceId.type",
            sort: "-isPreferred",
          },
        );
      },
      getItemId: (item: any) => item._id,
      getChipLabel: (item: any) => item.name,
      getChipSubLabel: (item: any) =>
        getLabelFromOptions(
          LEVELS_SPORT_OPTIONS[
            item.spaceId?.type as keyof typeof LEVELS_BY_SPORT
          ],
          item.level,
        ),
      renderChipIcon: (item: any) => (
        <TypeSpaceIcon type={item.spaceId?.type} />
      ),
      chipsCountLabel: "team selezionati",
      components: { Option: CustomTeamOptionRHF },
    },
  ];

  return (
    <FormProvider {...methods}>
      <GenericModalV2
        size="xl"
        onClose={onClose}
        isOpen={!!isOpen}
        Header={
          <Dialog.Header style={{ padding: 0, paddingBottom: "16px" }}>
            <Typhography size="bold-24">
              {isEditMode ? "Modifica" : "Nuovo"} Utente
            </Typhography>
            <Typhography size="regular-14" style={{ color: "var(--surfaces-5)", marginTop: 4 }}>
              {isEditMode
                ? "Modifica le informazioni dell'utente"
                : "Compila i dati per aggiungere un nuovo utente"}
            </Typhography>
          </Dialog.Header>
        }
      >
        <div className={style.formContainer}>
          {/* Sezione 1: Informazioni personali */}
          <div className={style.sectionTitle}>Informazioni personali</div>
          <CustomFormV3 layout={personalInfoLayout} />

          <div className={style.divider} />

          {/* Sezione 2: Account */}
          <div className={style.sectionTitle}>Account</div>
          <CustomFormV3 layout={accountLayout} />

          {/* Sezione 3: Spazio e squadre (condizionale) */}
          {type !== USER_TYPES.ADMINISTRATOR && (
            <>
              <div className={style.divider} />
              <div className={style.sectionTitle}>Spazio e squadre</div>
              <CustomFormV3 layout={assignmentLayout} />
            </>
          )}
        </div>

        <div className={style.footer}>
          <CustomButton color="outline" onClick={onClose}>
            Chiudi
          </CustomButton>
          <CustomButton
            color="secondary"
            disabled={isDisableSaveButton}
            onClick={methods.handleSubmit(onSave, (e) => {
            })}
          >
            Salva
          </CustomButton>
        </div>
      </GenericModalV2>
      <PlanLimitReachedModal
        open={!!planLimitError}
        onClose={() => setPlanLimitError(null)}
        planName={planLimitError?.planName ?? ''}
        currentCount={planLimitError?.current ?? 0}
        maxAthletes={planLimitError?.max ?? 0}
      />
    </FormProvider>
  );
};

export default AddUserModal;
